import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { 
  Users, 
  Clock, 
  ChevronRight, 
  Sparkles,
  MapPin,
  Loader
} from 'lucide-react';

const SalonSelectorPage = () => {
    const { orgId } = useParams();
    const navigate = useNavigate();

    const { data: salon, isLoading } = useQuery({
        queryKey: ['salon-public', orgId],
        queryFn: () => axios.get(`/api/queue/salon/${orgId}`).then(res => res.data),
        enabled: !!orgId
    });

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
                <Loader className="w-10 h-10 text-blue-600 animate-spin mb-4" />
                <p className="text-xs font-black uppercase tracking-widest text-gray-400">Ouverture du salon...</p>
            </div>
        );
    }

    if (!salon || !salon.events || salon.events.length === 0) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6 text-gray-400">
                    <Sparkles className="w-10 h-10" />
                </div>
                <h1 className="text-3xl font-black text-gray-900 tracking-tighter mb-2">Aucun Artiste Disponible</h1>
                <p className="text-gray-500 max-w-sm">Le salon est actuellement fermé ou aucune file d'attente n'est active.</p>
                <button onClick={() => navigate('/')} className="mt-8 text-blue-600 font-bold uppercase tracking-widest text-xs">Retour</button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 p-6 sm:p-12 transition-colors">
            <div className="max-w-xl mx-auto space-y-10 animate-in slide-in-from-bottom-8 duration-700">
                
                {/* Salon Profile */}
                <div className="text-center">
                    <div className="w-20 h-20 bg-blue-600 rounded-[2rem] mx-auto mb-6 flex items-center justify-center text-white shadow-xl shadow-blue-600/20">
                        <Users className="w-10 h-10" />
                    </div>
                    <h2 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em] mb-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-full inline-block">Tableau de bord Client</h2>
                    <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tighter italic uppercase">{salon.name}</h1>
                    <div className="mt-4 flex items-center justify-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-widest">
                        <MapPin className="w-3 h-3" /> Paris, Studio Central
                    </div>
                </div>

                <div className="space-y-4">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-2">Choisissez votre artiste</p>
                    
                    {salon.events.map((artist: any) => (
                        <button 
                            key={artist.id}
                            onClick={() => navigate(`/join?eventId=${artist.id}`)}
                            className="w-full bg-white dark:bg-slate-900 border dark:border-slate-800 p-6 rounded-[2rem] shadow-xl shadow-gray-200/50 dark:shadow-none hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-between group text-left"
                        >
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 bg-gray-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center font-black text-xl text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors uppercase tracking-tight">
                                    {artist.name.charAt(0)}
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tighter uppercase">{artist.name}</h3>
                                    <div className="flex items-center gap-3 mt-1">
                                        <div className="flex items-center gap-1 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                            <Clock className="w-3 h-3" /> {artist.avgTimePerClient} min / client
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-full text-gray-400 group-hover:bg-blue-600 group-hover:text-white transition-all">
                                <ChevronRight className="w-5 h-5" />
                            </div>
                        </button>
                    ))}
                </div>

                <div className="text-center pt-8">
                     <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">Propulsé par QueueFlow Digital</p>
                </div>
            </div>
        </div>
    );
};

export default SalonSelectorPage;
